import { ButtonDefaultVariants, ButtonShadedVariants } from '@library/common/utils/class-variants';
import { VariantProps, cva } from 'class-variance-authority';
import { ButtonHTMLAttributes, DetailedHTMLProps, FC, ForwardedRef, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import styles from './Button.module.scss';

const floatingButtonVariants = cva(
    [
        'inline-flex items-center justify-center rounded-full',
        'whitespace-nowrap',
        'font-semibold',
        'cursor-pointer',
        'shadow-[0px_2px_8px_rgba(0,0,0,0.1),0px_2px_8px_rgba(0,0,0,0.1)]',
        'transition-all duration-200 hover:brightness-110',
    ],
    {
        variants: {
            color: {
                primary: [],
                secondary: [],
                success: [],
                danger: [],
                warning: [],
            },
            variant: {
                default: [],
                shaded: [styles['shaded-elevation']],
            },
            size: {
                xs: ['text-sm w-10 h-10'],
                sm: ['text-base w-11 h-11'],
                normal: ['text-lg w-12 h-12'],
                lg: ['text-xl w-[3.25rem] h-[3.25rem]'],
                xl: ['text-2xl w-14 h-14'],
            },
        },
        compoundVariants: [...ButtonDefaultVariants, ...ButtonShadedVariants],
        defaultVariants: {
            color: 'primary',
            variant: 'default',
            size: 'xl',
        },
    }
);

export interface FloatingButtonProps
    extends Omit<DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>, 'color'>,
        VariantProps<typeof floatingButtonVariants> {
    ref?: ForwardedRef<HTMLButtonElement>;
}

const FloatingButton = forwardRef<HTMLButtonElement, FloatingButtonProps>(
    (props: FloatingButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
        const { children, variant, color, size, className, ...rest } = props;

        return (
            <button
                ref={ref}
                {...rest}
                className={twMerge(floatingButtonVariants({ color, variant, size, className }))}
            >
                {children}
            </button>
        );
    }
);

export default FloatingButton;
